import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaBell, FaTimes, FaCheck, FaCheckDouble, FaCog } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { webSocketService } from "../services/websocketService";
import { notificationService } from "../services/notificationService";
import NotificationItem from "./NotificationItem";
import NotificationSettings from "./NotificationSettings";

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'transaction', 'security', 'system'
  const [isLoading, setIsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(false);
  const { user } = useAuth();
  const userId = user?.id;
  const panelRef = useRef(null);

  // Fermer le panel si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Charger les notifications depuis l'API
  const loadNotifications = useCallback(async (page = 1, filterType = 'all') => {
    if (!userId) return;
    
    setIsLoading(true);
    try {
      const params = {
        page,
        limit: 20
      };
      
      if (filterType !== 'all') {
        if (filterType === 'unread') {
          params.read = false;
        } else {
          params.type = filterType.toUpperCase();
        }
      }

      const response = await notificationService.getNotifications(params);
      
      // La réponse est maintenant normalisée par le service
      const formattedNotifications = notificationService.formatNotificationData(response.notifications);
      
      if (page === 1) {
        setNotifications(formattedNotifications);
      } else {
        setNotifications(prev => [...prev, ...formattedNotifications]);
      }
      
      // Utiliser la pagination normalisée
      const currentPageNum = response.pagination?.current || page;
      const totalPages = response.pagination?.pages || 1;
      setHasMoreNotifications(currentPageNum < totalPages);
      setCurrentPage(page);
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
      notificationService.error('Erreur lors du chargement des notifications');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Charger le nombre de notifications non lues
  const loadUnreadCount = useCallback(async () => {
    if (!userId) return;
    
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Erreur lors du chargement du nombre de notifications non lues:', error);
    }
  }, [userId]);

  // Initialisation et chargement des données
  useEffect(() => {
    if (!userId) return;

    loadNotifications(1, filter);
    loadUnreadCount();

    // Souscrire aux notifications via WebSocket
    const unsubscribe = webSocketService.registerHandler(
      "notification",
      (data) => {
        // Traiter la notification reçue
        notificationService.processWebSocketNotification(data);
        
        // Recharger les notifications et le compteur
        loadNotifications(1, filter);
        loadUnreadCount();
      },
    );

    // Écouter l'événement personnalisé de nouvelle notification
    const handleNewNotification = () => {
      loadNotifications(1, filter);
      loadUnreadCount();
    };

    window.addEventListener('newNotification', handleNewNotification);

    return () => {
      unsubscribe();
      window.removeEventListener('newNotification', handleNewNotification);
    };
  }, [userId, filter, loadNotifications, loadUnreadCount]);

  // Gérer le changement de filtre
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    loadNotifications(1, newFilter);
  };

  // Charger plus de notifications (pagination)
  const loadMoreNotifications = () => {
    if (!isLoading && hasMoreNotifications) {
      loadNotifications(currentPage + 1, filter);
    }
  };

  // Marquer une notification comme lue
  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        await notificationService.markAsRead(notification._id);
        
        // Mettre à jour l'état local
        setNotifications(prev => 
          prev.map(n => 
            n._id === notification._id 
              ? { ...n, read: true, readAt: new Date() }
              : n
          )
        );
        
        // Mettre à jour le compteur
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la notification:', error);
        notificationService.error('Erreur lors de la mise à jour de la notification');
      }
    }
  };

  // Gérer les actions des notifications
  const handleNotificationAction = async (notification, action) => {
    try {
      switch (action.action) {
        case 'view_transaction':
          if (action.url) {
            window.location.href = action.url;
          }
          break;
        case 'view_wallet':
          window.location.href = '/wallet';
          break;
        case 'view_affiliate':
          window.location.href = '/affiliation';
          break;
        case 'view_promotion':
          if (action.url) {
            window.location.href = action.url;
          }
          break;
        default:
          console.log('Action non gérée:', action);
      }
      
      // Marquer comme lue après l'action
      if (!notification.read) {
        await handleNotificationClick(notification);
      }
    } catch (error) {
      console.error('Erreur lors de l\'exécution de l\'action:', error);
    }
  };

  // Marquer toutes les notifications comme lues
  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      
      // Mettre à jour l'état local
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true, readAt: new Date() }))
      );
      setUnreadCount(0);
      
      notificationService.success('Toutes les notifications ont été marquées comme lues');
    } catch (error) {
      console.error('Erreur lors de la mise à jour des notifications:', error);
      notificationService.error('Erreur lors de la mise à jour des notifications');
    }
  };

  // Supprimer une notification
  const handleDeleteNotification = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Mettre à jour l'état local
      const deletedNotification = notifications.find(n => n._id === notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      
      // Mettre à jour le compteur si la notification n'était pas lue
      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      notificationService.success('Notification supprimée');
    } catch (error) {
      console.error('Erreur lors de la suppression de la notification:', error);
      notificationService.error('Erreur lors de la suppression de la notification');
    }
  };

  // Options de filtre avec compteurs
  const filterOptions = [
    { 
      value: 'all', 
      label: 'Toutes', 
      count: notifications.length 
    },
    { 
      value: 'unread', 
      label: 'Non lues', 
      count: notifications.filter(n => !n.read).length 
    },
    { 
      value: 'transaction', 
      label: 'Transactions', 
      count: notifications.filter(n => n.type === 'TRANSACTION').length 
    },
    { 
      value: 'security', 
      label: 'Sécurité', 
      count: notifications.filter(n => n.type === 'SECURITY').length 
    },
    { 
      value: 'system', 
      label: 'Système', 
      count: notifications.filter(n => n.type === 'SYSTEM').length 
    },
    { 
      value: 'promotion', 
      label: 'Promotions', 
      count: notifications.filter(n => n.type === 'PROMOTION').length 
    }
  ];

  // Filtrer les notifications selon le filtre actuel
  const filteredNotifications = notifications.filter(notification => {
    switch (filter) {
      case 'unread':
        return !notification.read;
      case 'transaction':
        return notification.type === 'TRANSACTION';
      case 'security':
        return notification.type === 'SECURITY';
      case 'system':
        return notification.type === 'SYSTEM';
      case 'promotion':
        return notification.type === 'PROMOTION';
      default:
        return true;
    }
  });

  const sortedNotifications = notifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  if (!userId) return null;

  return (
    <div className="relative" ref={panelRef}>
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full bg-slate-700/50 hover:bg-slate-700/70 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
        title="Notifications"
      >
        <FaBell className="w-5 h-5 text-slate-300" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </motion.span>
        )}
      </button>

      {/* Notification Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="notification-panel"
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95, transition: { duration: 0.2 } }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="absolute right-0 lg:right-[-50px] xl:right-[-100px] mt-3 w-[420px] max-w-[95vw] rounded-2xl shadow-2xl bg-slate-800 border border-slate-600 focus:outline-none z-50 overflow-hidden backdrop-blur-xl bg-slate-800/95"
            role="dialog"
            aria-modal="true"
            aria-labelledby="notification-heading"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-700">
              <div className="flex items-center justify-between">
                <h3 id="notification-heading" className="text-xl font-bold text-white flex items-center gap-2">
                  <FaBell className="text-blue-400" />
                  Notifications
                  {unreadCount > 0 && (
                    <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded-full font-medium">
                      {unreadCount}
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  {notifications.some(n => !n.read) && (
                    <button
                      onClick={markAllAsRead}
                      className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      title="Marquer tout comme lu"
                    >
                      <FaCheckDouble className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded-lg transition-all duration-200"
                    title="Paramètres"
                  >
                    <FaCog className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded-lg transition-all duration-200"
                    title="Fermer"
                  >
                    <FaTimes className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="mt-4 flex flex-wrap gap-2">
                {filterOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleFilterChange(option.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                      filter === option.value
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'bg-slate-700 text-gray-300 hover:bg-slate-600 hover:text-white'
                    }`}
                  >
                    {option.label}
                    {option.count > 0 && (
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded text-xs ${
                        filter === option.value ? 'bg-blue-600' : 'bg-slate-600'
                      }`}>
                        {option.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="border-b border-slate-700">
                <NotificationSettings onClose={() => setShowSettings(false)} />
              </div>
            )}

            {/* Content */}
            <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
              {isLoading && notifications.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-slate-400">Chargement des notifications...</p>
                </div>
              ) : filteredNotifications.length > 0 ? (
                <div className="divide-y divide-slate-700/50">
                  <AnimatePresence initial={false}>
                    {filteredNotifications.map((notification, index) => (
                      <NotificationItem
                        key={notification._id}
                        notification={notification}
                        onClick={handleNotificationClick}
                        onAction={handleNotificationAction}
                        onDelete={handleDeleteNotification}
                        index={index}
                      />
                    ))}
                  </AnimatePresence>
                  
                  {/* Load More Button */}
                  {hasMoreNotifications && (
                    <div className="px-6 py-4 text-center">
                      <button
                        onClick={loadMoreNotifications}
                        disabled={isLoading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                      >
                        {isLoading ? 'Chargement...' : 'Charger plus'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-6 py-12 text-center">
                  <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaBell className="h-8 w-8 text-slate-500" />
                  </div>
                  <p className="text-slate-300 font-medium mb-2">
                    {filter === 'unread' ? 'Aucune notification non lue' : 'Aucune notification'}
                  </p>
                  <p className="text-slate-500 text-sm">
                    {filter === 'all' 
                      ? 'Vous êtes à jour !' 
                      : `Aucune notification dans la catégorie "${filterOptions.find(f => f.value === filter)?.label}"`
                    }
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCenter;
